import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

import jwt, { JwtPayload } from 'jsonwebtoken'

export enum STORE_ACTIONS {
  SESSION_STORE_LOGIN = 'userSession/login',
  SESSION_STORE_LOGOUT = 'userSession/logout',
  SESSION_STORE_SYNCHRONISE_WITH_LOCAL_STORAGE = 'userSession/synchroniseWithLocalStorage'
}

export enum STORE_GETTERS {
  SESSION_GET_TOKEN = 'userSession/token',
  SESSION_GET_IS_LOGGED_IN = 'userSession/isLoggedIn',
  SESSION_GET_FIRST_NAME = 'userSession/firstName',
  SESSION_GET_LAST_NAME = 'userSession/lastName',
  SESSION_GET_EMAIL = 'userSession/email',
  SESSION_GET_USERNAME = 'userSession/username',
  SESSION_GET_USER_FULL_NAME = 'userSession/userFullName',
  SESSION_GET_USER_FULL_NAME_WITH_USERNAME = 'userSession/userFullNameWithUsername',
  SESSION_GET_USER_FULL_NAME_WITH_EMAIL = 'userSession/userFullNameWithEmail'
}

export const LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN: string = 'authorization-token'
export const LOCAL_STORAGE_KEY_TOKEN: string = 'token'

@Module({ namespaced: true })
export default class UserSessionStore extends VuexModule {
  public readonly LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN: string = LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN

  private _token: string | null = null
  private _isLoggedIn: boolean = false
  private _firstName: string | null = null
  private _lastName: string | null = null
  private _email: string | null = null
  private _username: string | null = null

  @Mutation
  private storeTokenMutation(token: string): void {
    localStorage.setItem(this.LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN, token)
  }

  @Mutation
  private synchroniseWithLocalStorageMutation(): void {
    this._token = localStorage.getItem(this.LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN)
    this._isLoggedIn = !!this._token
  }

  @Mutation
  private decodeTokenInformationsMutation(): void {
    if (this._token) {
      const decodedToken: JwtPayload = jwt.decode(this._token || '') as JwtPayload

      if (decodedToken) {
        this._firstName = decodedToken.firstName
        this._lastName = decodedToken.lastName
        this._email = decodedToken.email
        this._username = decodedToken.username
      }
    }
  }

  @Mutation
  private clearTokenMutation(): void {
    localStorage.removeItem(this.LOCAL_STORAGE_KEY_AUTHORIZATION_TOKEN)
  }

  @Mutation
  private clearUserInformationsMutation(): void {
    this._firstName = null
    this._lastName = null
    this._email = null
    this._username = null
  }

  @Action
  public login(token: string): void {
    this.context.commit('storeTokenMutation', token)
    this.context.commit('synchroniseWithLocalStorageMutation')
    this.context.commit('decodeTokenInformationsMutation')
  }

  @Action
  public logout(): void {
    this.context.commit('clearTokenMutation')
    this.context.commit('synchroniseWithLocalStorageMutation')
    this.context.commit('clearUserInformationsMutation')
  }

  @Action
  public synchroniseWithLocalStorage(): void {
    this.context.commit('synchroniseWithLocalStorageMutation')
    this.context.commit('decodeTokenInformationsMutation')
  }

  private get token(): string | null {
    return this._token
  }

  private get isLoggedIn(): boolean {
    return this._isLoggedIn
  }

  private get firstName(): string | null {
    return this._firstName
  }

  private get lastName(): string | null {
    return this._lastName
  }

  private get email(): string | null {
    return this._email
  }

  private get username(): string | null {
    return this._username
  }

  private get userFullName(): string {
    return `${this.firstName} ${this.lastName}`
  }

  private get userFullNameWithUsername(): string {
    return `${this.userFullName} (${this.username})`
  }

  private get userFullNameWithEmail(): string {
    return `${this.userFullName} (${this.email})`
  }
}
